import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { formatCalendarDate, formatTimeOnly } from '../../utils/dateTimeUtils';

const REASON_CHIP = {
  primary_on_leave: { label: 'Primary — on leave', color: 'warning' },
  primary: { label: 'Primary teacher', color: 'info' },
  on_leave: { label: 'On leave', color: 'warning' },
  busy: { label: 'Already busy at this time', color: 'error' },
  outside_hours: { label: 'Outside working hours', color: 'default' },
};

function normalizeSubstituteResponse(data) {
  if (!data) return { available: [], partial: [], unavailable: [] };
  if (Array.isArray(data)) return { available: data, partial: [], unavailable: [] };
  return {
    available: data.available || data.exact || data.teachers || [],
    partial: data.partial || [],
    unavailable: data.unavailable || [],
  };
}

export function parseSubstituteTeachersResponse(res) {
  const data = res?.data?.data;
  return {
    ...normalizeSubstituteResponse(data),
    recentSubstitutions: Array.isArray(data?.recentSubstitutions) ? data.recentSubstitutions : [],
  };
}

function suggestionWhen(item) {
  const date = formatCalendarDate(item?.classDate);
  const start = formatTimeOnly(item?.startTime);
  const end = formatTimeOnly(item?.endTime);
  if (start && end) return `${date} · ${start}–${end}`;
  return date;
}

function suggestionKind(item) {
  return item?.kind === 'cover' ? 'Cover' : 'Leave';
}

function RecentSubstituteSuggestions({ items = [], value, teachersById, onChange }) {
  if (!items.length) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        Recent substitutes for this batch
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {items.map((item, index) => {
          const id = String(item.teacherId || '');
          const selectable = !!teachersById[id];
          const selected = !!value && value === id;
          const label = `${suggestionWhen(item)} · ${item.teacherName} · ${suggestionKind(item)}`;
          if (!selectable) {
            return (
              <Box
                key={`${item.kind}-${item.classDate}-${id}-${index}`}
                sx={{ px: 1.25, py: 0.75, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            );
          }
          return (
            <Button
              key={`${item.kind}-${item.classDate}-${id}-${index}`}
              size="small"
              fullWidth
              variant={selected ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => onChange(id)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                textAlign: 'left',
                whiteSpace: 'normal',
                lineHeight: 1.35,
                py: 0.75,
              }}
            >
              {label}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

function teacherClassesLabel(t) {
  if (t.classesToday == null) return '';
  return ` — ${t.classesToday} class${t.classesToday === 1 ? '' : 'es'} today`;
}

function supervisorSuffix(t) {
  if (t.reportingGroup === 'my_team' || !t.supervisorName) return '';
  return ` · Supervisor: ${t.supervisorName}`;
}

function shadowSuffix(t) {
  if (t.isBatchShadow) return ' · Shadow (this batch)';
  if (t.isShadowTeacher) return ' · Shadow';
  return '';
}

function renderTeacherOption(t) {
  return `${t.name}${shadowSuffix(t)}${teacherClassesLabel(t)}${supervisorSuffix(t)}`;
}

/** MenuItem must be a direct Select child — do not wrap in a custom component. */
function renderAvailableMenuItem(t, { showShadowChip = false, keyPrefix = '' } = {}) {
  const id = String(t.id);
  return (
    <MenuItem key={`${keyPrefix}${id}`} value={id}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {renderTeacherOption(t)}
        </Typography>
        {showShadowChip || t.isShadowTeacher ? (
          <Chip size="small" label="Shadow" color="info" variant="outlined" />
        ) : null}
      </Stack>
    </MenuItem>
  );
}

export default function SubstituteTeacherPicker({
  available = [],
  unavailable = [],
  loading = false,
  value,
  onChange,
  label = 'Substitute teacher',
  emptyMessage = 'No teachers found for this slot.',
  required = true,
  allowEmpty = false,
  emptyLabel = 'None',
  disabled = false,
  excludeIds = [],
  recentSubstitutions = [],
}) {
  const valueStr = value ? String(value) : '';
  const excludeSet = useMemo(
    () => new Set((excludeIds || []).filter(Boolean).map((id) => String(id))),
    [excludeIds]
  );
  const filteredAvailable = useMemo(
    () => available.filter((t) => !excludeSet.has(String(t.id))),
    [available, excludeSet]
  );
  const filteredUnavailable = useMemo(
    () => unavailable.filter((t) => !excludeSet.has(String(t.id))),
    [unavailable, excludeSet]
  );
  const teachersById = useMemo(() => {
    const map = {};
    [...available, ...unavailable].forEach((t) => {
      map[String(t.id)] = t;
    });
    return map;
  }, [available, unavailable]);
  const hasTeachers = filteredAvailable.length > 0 || filteredUnavailable.length > 0;
  const shadowAvailable = filteredAvailable.filter((t) => t.isShadowTeacher);
  const otherAvailable = filteredAvailable.filter((t) => !t.isShadowTeacher);
  const myTeamAvailable = otherAvailable.filter((t) => t.reportingGroup !== 'other');
  const otherTeamAvailable = otherAvailable.filter((t) => t.reportingGroup === 'other');
  const primaryTeachers = filteredUnavailable.filter((t) => t.isPrimaryTeacher);
  const overrideTeachers = filteredUnavailable.filter((t) => !t.isPrimaryTeacher);
  const selectedOverride = valueStr
    ? overrideTeachers.find((t) => String(t.id) === valueStr)
    : null;

  const selectableTeachers = useMemo(() => {
    const map = {};
    [...filteredAvailable, ...filteredUnavailable].forEach((t) => {
      if (t.selectable === false) return;
      map[String(t.id)] = t;
    });
    return map;
  }, [filteredAvailable, filteredUnavailable]);

  const suggestionBlock = (
    <RecentSubstituteSuggestions
      items={recentSubstitutions}
      value={valueStr}
      teachersById={selectableTeachers}
      onChange={onChange}
    />
  );

  if (loading) {
    return <CircularProgress size={22} />;
  }

  if (!hasTeachers && !allowEmpty) {
    return (
      <Stack spacing={1}>
        {suggestionBlock}
        <Alert severity="warning" sx={{ py: 0 }}>
          {emptyMessage}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {suggestionBlock}
      <FormControl fullWidth size="small" required={required} disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={valueStr}
          label={label}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          renderValue={(selected) => {
            if (!selected) return allowEmpty ? emptyLabel : '';
            const t = teachersById[String(selected)];
            return t ? renderTeacherOption(t) : '';
          }}
        >
          {allowEmpty ? (
            <MenuItem value="">
              <em>{emptyLabel}</em>
            </MenuItem>
          ) : null}

          {shadowAvailable.length > 0 ? (
            <ListSubheader sx={{ lineHeight: 2 }}>Shadow teachers — available</ListSubheader>
          ) : null}
          {shadowAvailable.map((t) => renderAvailableMenuItem(t, { showShadowChip: true, keyPrefix: 'shadow-' }))}

          {shadowAvailable.length > 0 && (myTeamAvailable.length > 0 || otherTeamAvailable.length > 0) ? (
            <Divider component="li" />
          ) : null}

          {myTeamAvailable.length > 0 ? (
            <ListSubheader sx={{ lineHeight: 2 }}>My team — available</ListSubheader>
          ) : null}
          {myTeamAvailable.map((t) => renderAvailableMenuItem(t))}

          {otherTeamAvailable.length > 0 ? (
            <ListSubheader sx={{ lineHeight: 2 }}>Other teachers — available</ListSubheader>
          ) : null}
          {otherTeamAvailable.map((t) => renderAvailableMenuItem(t))}

          {primaryTeachers.length > 0 ? <Divider component="li" /> : null}
          {primaryTeachers.length > 0 ? (
            <ListSubheader sx={{ lineHeight: 2 }}>Primary teacher</ListSubheader>
          ) : null}
          {primaryTeachers.map((t) => {
            const id = String(t.id);
            const chip = REASON_CHIP[t.reason] || {
              label: t.reasonLabel || 'Primary teacher',
              color: 'warning',
            };
            return (
              <MenuItem key={`primary-${id}`} value={id} disabled>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {renderTeacherOption(t)}
                  </Typography>
                  <Chip size="small" label={t.reasonLabel || chip.label} color={chip.color} variant="outlined" />
                </Stack>
              </MenuItem>
            );
          })}

          {(shadowAvailable.length > 0 ||
            myTeamAvailable.length > 0 ||
            otherTeamAvailable.length > 0 ||
            primaryTeachers.length > 0) &&
          overrideTeachers.length > 0 ? (
            <Divider component="li" />
          ) : null}

          {overrideTeachers.length > 0 ? (
            <ListSubheader sx={{ lineHeight: 2 }}>Other teachers (override)</ListSubheader>
          ) : null}
          {overrideTeachers.map((t) => {
            const id = String(t.id);
            const chip = REASON_CHIP[t.reason] || {
              label: t.reasonLabel || 'Unavailable',
              color: 'default',
            };
            return (
              <MenuItem key={`unavail-${id}`} value={id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {renderTeacherOption(t)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t.reasonLabel || chip.label}
                    color={chip.color}
                    variant="outlined"
                  />
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {selectedOverride ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          You selected <strong>{selectedOverride.name}</strong> who is marked as{' '}
          <strong>{selectedOverride.reasonLabel || REASON_CHIP[selectedOverride.reason]?.label}</strong>.
          You can override, but this may conflict with their schedule or working hours.
        </Alert>
      ) : null}

      {filteredAvailable.length === 0 && overrideTeachers.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          No teachers are fully available. You may still assign someone from the override list above.
        </Typography>
      ) : null}
    </Stack>
  );
}
